<template>
  <div class="fatturazione-extra-form">
    <div v-if="checkLoader" class="loader">
      Controllo fatture automatiche in corso...
    </div>

    <div v-else-if="!showForm" class="response-msg error">
      {{ responseMsg }}
      <div class="mt-3">
        <button type="button" class="btn btn-primary" @click="checkFatturabileAuto" :disabled="checkLoader">
          {{ checkLoader ? 'Controllo in corso...' : 'Riprova' }}
        </button>
      </div>
    </div>

    <form v-else @submit.prevent="onSubmit">
      <div class="row">
        <div class="col">
          <label>Partitario</label>
          <select v-model="form.partitario" class="form-control">
            <option value="">-- Seleziona partitario --</option>
            <option v-for="part in partitari" :key="part.id" :value="part.code">
              {{ part.code }}
            </option>
          </select>
        </div>
        <div class="col">
          <label>Tipologia</label>
          <select v-model="form.type_doc" class="form-control">
            <option v-for="opt in typeDocOptions" :key="opt.val" :value="opt.val">
              {{ opt.label }}
            </option>
          </select>
        </div>
        <div class="col">
          <label>Data emissione</label>
          <input v-model="form.data_emissione" type="date" class="form-control" />
        </div>
        <div class="col">
          <label>Data scadenza</label>
          <input v-model="form.data_scadenza" type="date" class="form-control" />
        </div>
      </div>

      <div class="row row-wide">
        <div class="form-group col-half">
          <label>Seleziona Cliente</label>
          <div class="autocomplete-wrapper">
            <input v-model="clientInput" @input="onClientInput" @focus="onClientFocus" @blur="(e) => onClientBlur(e)"
              @keydown="(e) => onClearKey(e, 'client')" type="text" class="form-control"
              placeholder="Codice cliente o denominazione" autocomplete="off" />
            <ul v-if="showClientDropdown && clientiOptions.length" class="autocomplete-list">
              <li v-for="c in clientiOptions" :key="c.id" @mousedown.prevent="selectClient(c)">
                {{ c.denominazione_completa }} - {{ c.codfis }} - {{ c.piva }}
              </li>
            </ul>
          </div>
        </div>

        <div class="form-group col-half">
          <label>Seleziona Contratto</label>
          <div class="autocomplete-wrapper">
            <input ref="contrattoInput" :value="contrattoValue" @focus="onContrattoFocus" @blur="(e) => onContrattoBlur(e)"
              @keydown="(e) => onClearKey(e, 'contratto')" type="text" class="form-control"
              placeholder="Seleziona un contratto" />
            <ul v-if="showContrattiDropdown && contratti.length" class="autocomplete-list">
              <li v-for="contratto in contratti" :key="contratto.id" @mousedown.prevent="selectContratto(contratto)">
                {{ contratto.contratto_code }} - {{ contratto.type_pay_name }} - IBAN: {{ contratto.iban }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="row row-wide">
        <div class="form-group col-half">
          <label>POD</label>
          <div class="autocomplete-wrapper">
            <input v-model="podInput" @input="onPodInput" @focus="onPodFocus" @blur="(e) => onPodBlur(e)"
              @keydown="(e) => onClearKey(e, 'pod')" type="text" class="form-control" autocomplete="off" />
            <ul v-if="showPodDropdown && podOptions.length" class="autocomplete-list">
              <li v-for="p in podOptions" :key="p.id" @mousedown.prevent="selectPod(p)">
                {{ p.code }}
              </li>
            </ul>
          </div>
        </div>

        <div class="form-group col-half">
          <label>PDR</label>
          <div class="autocomplete-wrapper">
            <input v-model="pdrInput" @input="onPdrInput" @focus="onPdrFocus" @blur="(e) => onPdrBlur(e)"
              @keydown="(e) => onClearKey(e, 'pdr')" type="text" class="form-control" autocomplete="off" />
            <ul v-if="showPdrDropdown && pdrOptions.length" class="autocomplete-list">
              <li v-for="p in pdrOptions" :key="p.id" @mousedown.prevent="selectPdr(p)">
                {{ p.code }}
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-12">
          <label>Pagamento</label>
          <select v-model="form.cod_type_pay" class="form-control" @keydown="(e) => onClearKey(e, 'pagamento')">
            <option value="">-- Seleziona pagamento --</option>
            <option v-for="pay in pagamenti" :key="pay.id" :value="pay.id">
              {{ pay.name }}
            </option>
          </select>
        </div>
      </div>

      <div class="voci-section">
        <div v-for="i in 10" :key="i" class="row voce-row">
          <div class="col quantita-col">
            <label>QTA</label>
            <input v-model="form.voci[i - 1].qta" type="number" min="1" step="1" class="form-control" />
          </div>
          <div class="col voce-col">
            <label>Seleziona Voce</label>
            <div class="voce-selector">
              <div class="voce-display" @click="openVoceModal(i - 1)">
                <span v-if="vociInput[i - 1]" class="voce-selected">{{ vociInput[i - 1] }}</span>
                <span v-else class="voce-placeholder">Clicca per selezionare una voce</span>
              </div>
              <button v-if="vociInput[i - 1]" type="button" class="btn-clear-voce" @click="clearVoce(i - 1)">
                ✕
              </button>
            </div>
          </div>
          <div class="col aliquota-col">
            <label>Aliquota</label>
            <input v-model="form.voci[i - 1].aliquota" type="text" class="form-control" />
          </div>
          <div class="col importo-col">
            <label>Importo</label>
            <input v-model="form.voci[i - 1].importo" type="number" step="0.01" class="form-control" />
            <div class="importo-totale-hint" v-if="calculateVoceTotal(i - 1) !== null">
              Totale: {{ calculateVoceTotal(i - 1) }}
            </div>
          </div>
          <div class="col descrizione-col">
            <label>Descrizione</label>
            <input v-model="form.voci[i - 1].descrizione" type="text" class="form-control" />
          </div>
        </div>
      </div>

      <!-- Modal per selezione voci -->
      <div v-if="showVoceModal" class="modal-overlay" @click="closeVoceModal">
        <div class="modal-content" @click.stop>
          <div class="modal-header">
            <h3>Seleziona Voce</h3>
            <button type="button" class="modal-close" @click="closeVoceModal">✕</button>
          </div>
          <div class="modal-body">
            <div class="search-section">
              <input 
                v-model="modalSearchTerm" 
                @input="onModalSearch" 
                type="text" 
                class="modal-search" 
                placeholder="Cerca per codice o descrizione..."
                autofocus
              />
            </div>
            <div class="voci-list">
              <div v-if="modalLoading" class="loading-spinner">
                <div class="spinner"></div>
                <span>Caricamento...</span>
              </div>
              <div v-else-if="modalVociOptions.length === 0" class="no-results">
                <span>Nessuna voce trovata</span>
              </div>
              <div v-else class="voci-grid">
                <div 
                  v-for="voce in modalVociOptions" 
                  :key="voce.id" 
                  class="voce-item"
                  @click="selectVoceFromModal(voce)"
                >
                  <div class="voce-code">{{ voce.codice || 'N/A' }}</div>
                  <div class="voce-desc">{{ voce.descrizione }}</div>
                  <div class="voce-category">{{ voce.categoria || 'Generale' }}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="row mt-3">
        <div class="col">
          <button type="button" class="btn btn-primary" @click="onSimula">
            Simula
          </button>
        </div>
        <div class="col">
          <button type="submit" class="btn btn-danger">
            Fattura 
          </button>
        </div>
      </div>
    </form>

    <div v-if="showForm && loader" class="loader">Caricamento....</div>
    <div v-if="showForm && responseMsg" class="response-msg" :class="{ error: !responseSuccess }">
      {{ responseMsg }}
    </div>
  </div>
</template>


<script>
import { ref, reactive, onMounted, getCurrentInstance, watch, computed } from 'vue';
import { useStore } from 'vuex';
import Service from '@/services/ask-fatturazione/fatturazione-service';




export default {
  setup() {
    const store = useStore();
    const loader = ref(false);
    const responseMsg = ref('');
    const responseSuccess = ref(true);
    const pagamenti = ref([]);
    const partitari = ref([]);
    const checkLoader = ref(true);
    const fatturabileCheck = ref(null);
    const showForm = ref(true);
    const contratti = ref([]);
    const showContrattiDropdown = ref(false);
    const contrattoSelezionato = ref(null);
    const validationErrors = ref({});
    const typeDocOptions = [
      { val: 'TD01', label: 'Fattura' },
      { val: 'TD02', label: 'Acconto/Anticipo su fattura' },
      { val: 'TD03', label: 'Acconto/Anticipo su parcella' },
      { val: 'TD05', label: 'Nota di Debito' },
      { val: 'TD04', label: 'Nota di Credito' },
      { val: 'TD06', label: 'Parcella' },
      { val: 'TD20', label: 'Autofattura (per denuncia mancato ricevimento da parte del fornitore)' },
    ];
    const today = new Date().toISOString().slice(0, 10);
    const form = reactive({
      partitario: '',
      type_doc: 'TD01',
      data_emissione: today,
      data_scadenza: today,
      cod_cliente: '',
      pod: '',
      pdr: '',
      cod_type_pay: '',
      voci: Array.from({ length: 10 }, () => ({ cod_voce: '', qta: 1, aliquota: '', importo: '', descrizione: '' }))
    });

    const clientInput = ref('');
    const clientiOptions = ref([]);
    const showClientDropdown = ref(false);
    const onClientInput = async () => {
      for (let i = 0; i < showVociDropdown.value.length; i++) {
        showVociDropdown.value[i] = false;
      }
      
      if (clientInput.value.length >= 2) {
        clientiOptions.value = await service.autocompleteClient(clientInput.value);
        showClientDropdown.value = true;
      } else {
        clientiOptions.value = [];
        showClientDropdown.value = false;
      }

      if (clientInput.value !== (contrattoSelezionato.value ? contrattoSelezionato.value.contratto_code : '')) {
        contratti.value = [];
        showContrattiDropdown.value = false;
        contrattoSelezionato.value = null;
        form.cod_type_pay = '';
      }
    };
    const selectClient = async (c) => {
      form.cod_cliente = c.id;
      clientInput.value = c.denominazione_completa;
      showClientDropdown.value = false;

      try {
        const result = await service.getContrattiByCliente(c.id);
        if (result.success && result.data && result.data.length > 0) {
          contratti.value = result.data;
          showContrattiDropdown.value = true;
        } else {
          contratti.value = [];
          showContrattiDropdown.value = false;
        }
      } catch (error) {
        console.error('Errore nel caricamento contratti:', error);
        contratti.value = [];
        showContrattiDropdown.value = false;
      }
    };
    const onClientFocus = () => {
      for (let i = 0; i < showVociDropdown.value.length; i++) {
        showVociDropdown.value[i] = false;
      }
      showContrattiDropdown.value = false;
      showPodDropdown.value = false;
      showPdrDropdown.value = false;
      
      if (clientiOptions.value.length) showClientDropdown.value = true;
    };
    const onClientBlur = (e) => {
      if (e.relatedTarget && e.relatedTarget.closest('.autocomplete-list')) {
        return;
      }
      setTimeout(() => { 
        showClientDropdown.value = false; 
      }, 200);
    };

    const contrattoValue = computed(() => {
      if (contrattoSelezionato.value) {
        return `${contrattoSelezionato.value.contratto_code} - ${contrattoSelezionato.value.type_pay_name}`;
      }
      return '';
    });

    const selectContratto = (contratto) => {
      contrattoSelezionato.value = contratto;
      showContrattiDropdown.value = false;
    };

    const onContrattoFocus = () => {
      for (let i = 0; i < showVociDropdown.value.length; i++) {
        showVociDropdown.value[i] = false;
      }
      showClientDropdown.value = false;
      showPodDropdown.value = false;
      showPdrDropdown.value = false;
      
      if (contratti.value.length) {
        showContrattiDropdown.value = true;
      }
    };

    const onContrattoBlur = (e) => {
      if (e.relatedTarget && e.relatedTarget.closest('.autocomplete-list')) {
        return;
      }
      setTimeout(() => {
        showContrattiDropdown.value = false;
      }, 200);
    };

    const podInput = ref('');
    const podOptions = ref([]);
    const showPodDropdown = ref(false);
    const onPodInput = async () => {
      for (let i = 0; i < showVociDropdown.value.length; i++) {
        showVociDropdown.value[i] = false;
      }
      
      if (podInput.value.length >= 2) {
        podOptions.value = await service.autocompletePod(podInput.value);
        showPodDropdown.value = true;
      } else {
        podOptions.value = [];
        showPodDropdown.value = false;
      }
    };
    const selectPod = (p) => {
      form.pod = p.code;
      podInput.value = p.code;
      showPodDropdown.value = false;
    };
    const onPodFocus = () => {
      for (let i = 0; i < showVociDropdown.value.length; i++) {
        showVociDropdown.value[i] = false;
      }
      showClientDropdown.value = false;
      showContrattiDropdown.value = false;
      showPdrDropdown.value = false;
      
      if (podOptions.value.length) showPodDropdown.value = true;
    };
    const onPodBlur = (e) => {
      if (e.relatedTarget && e.relatedTarget.closest('.autocomplete-list')) {
        return;
      }
      setTimeout(() => { 
        showPodDropdown.value = false; 
      }, 200);
      
    };

    const pdrInput = ref('');
    const pdrOptions = ref([]);
    const showPdrDropdown = ref(false);
    const onPdrInput = async () => {
      for (let i = 0; i < showVociDropdown.value.length; i++) {
        showVociDropdown.value[i] = false;
      }
      
      if (pdrInput.value.length >= 2) {
        pdrOptions.value = await service.autocompletePdr(pdrInput.value);
        showPdrDropdown.value = true;
      } else {
        pdrOptions.value = [];
        showPdrDropdown.value = false;
      }
    };
    const selectPdr = (p) => {
      form.pdr = p.code;
      pdrInput.value = p.code;
      showPdrDropdown.value = false;
    };
    const onPdrFocus = () => {
      for (let i = 0; i < showVociDropdown.value.length; i++) {
        showVociDropdown.value[i] = false;
      }
      showClientDropdown.value = false;
      showContrattiDropdown.value = false;
      showPodDropdown.value = false;
      
      if (pdrOptions.value.length) showPdrDropdown.value = true;
    };
    const onPdrBlur = (e) => {
      if (e.relatedTarget && e.relatedTarget.closest('.autocomplete-list')) {
        return;
      }
      setTimeout(() => { 
        showPdrDropdown.value = false; 
      }, 200);
    };

    const vociInput = ref(Array.from({ length: 10 }, () => ''));
    const vociOptions = ref(Array.from({ length: 10 }, () => []));
    const showVociDropdown = ref(Array.from({ length: 10 }, () => false));
    const vociBlurTimeout = Array.from({ length: 10 }, () => null);
    
    const showVoceModal = ref(false);
    const modalSearchTerm = ref('');
    const modalVociOptions = ref([]);
    const modalLoading = ref(false);
    const currentVoceIndex = ref(-1);
    
    const getDropdownZIndex = (idx) => {
      const activeIndex = showVociDropdown.value.findIndex(show => show);
      if (activeIndex === idx) {
        return 1050; 
      }
      return 1000 - idx; 
    };
    const onVoceInput = async (idx) => {
      for (let i = 0; i < showVociDropdown.value.length; i++) {
        if (i !== idx) {
          showVociDropdown.value[i] = false;
        }
      }
      
      showClientDropdown.value = false;
      showContrattiDropdown.value = false;
      showPodDropdown.value = false;
      showPdrDropdown.value = false;
      
      if (vociInput.value[idx].length >= 2) {
        vociOptions.value[idx] = await service.autocompleteFatturaVoci(vociInput.value[idx]);
        showVociDropdown.value[idx] = true;
      } else {
        vociOptions.value[idx] = [];
        showVociDropdown.value[idx] = false;
      }
    };
    const selectVoce = (idx, v) => {
      form.voci[idx].cod_voce = v.id;
      vociInput.value[idx] = v.descrizione;
      showVociDropdown.value[idx] = false;
    };
    
    const openVoceModal = async (idx) => {
      currentVoceIndex.value = idx;
      showVoceModal.value = true;
      modalSearchTerm.value = '';
      modalVociOptions.value = [];
      modalLoading.value = true;
      
      try {
        const allVoci = await service.getAllFatturaVoci();
        modalVociOptions.value = allVoci || [];
      } catch (error) {
        console.error('Errore nel caricamento voci:', error);
        modalVociOptions.value = [];
      } finally {
        modalLoading.value = false;
      }
    };
    
    const closeVoceModal = () => {
      showVoceModal.value = false;
      modalSearchTerm.value = '';
      modalVociOptions.value = [];
      currentVoceIndex.value = -1;
    };
    
    const onModalSearch = async () => {
      if (modalSearchTerm.value.length < 2) {
        try {
          const allVoci = await service.getAllFatturaVoci();
          modalVociOptions.value = allVoci || [];
        } catch (error) {
          console.error('Errore nel caricamento voci:', error);
          modalVociOptions.value = [];
        }
        return;
      }
      
      modalLoading.value = true;
      try {
        const results = await service.autocompleteFatturaVoci(modalSearchTerm.value);
        modalVociOptions.value = results || [];
      } catch (error) {
        console.error('Errore nella ricerca voci:', error);
        modalVociOptions.value = [];
      } finally {
        modalLoading.value = false;
      }
    };
    
    const calculateVoceTotal = (idx) => {
      const qty = Number(form.voci[idx]?.qta);
      const baseImporto = Number(form.voci[idx]?.importo);
      if (isNaN(qty) || qty <= 0 || isNaN(baseImporto) || baseImporto <= 0) {
        return null;
      }
      const total = baseImporto * qty;
      return total.toFixed(2);
    };

    
    const selectVoceFromModal = (voce) => {
      if (currentVoceIndex.value >= 0) {
        form.voci[currentVoceIndex.value].cod_voce = voce.id;
        vociInput.value[currentVoceIndex.value] = voce.descrizione;
        closeVoceModal();
      }
    };
    const onVoceFocus = (idx) => () => {
      for (let i = 0; i < showVociDropdown.value.length; i++) {
        if (i !== idx) {
          showVociDropdown.value[i] = false;
        }
      }
      
      showClientDropdown.value = false;
      showContrattiDropdown.value = false;
      showPodDropdown.value = false;
      showPdrDropdown.value = false;
      
      if (vociOptions.value[idx].length) showVociDropdown.value[idx] = true;
    };
    const onVoceBlur = (idx) => (e) => {
      if (e.relatedTarget && e.relatedTarget.closest('.autocomplete-list')) {
        return;
      }
      if (vociBlurTimeout[idx]) {
        clearTimeout(vociBlurTimeout[idx]);
      }
      vociBlurTimeout[idx] = setTimeout(() => { 
        showVociDropdown.value[idx] = false; 
      }, 200);
    };

    const loadPagamenti = async () => {
      pagamenti.value = await service.getTypePayList();
    };

    const loadPartitari = async () => {
      try {
        partitari.value = await service.getPartitariList();
      } catch (error) {
        console.error('Errore durante il caricamento dei partitari:', error);
        partitari.value = [];
      }
    };

    const checkFatturabileAuto = async () => {
      try {
        checkLoader.value = true;
        const result = await service.checkFatturabileExtraAuto();
        fatturabileCheck.value = result;
        showForm.value = result.success;
        if (!result.success) {
          responseMsg.value = result.message;
          responseSuccess.value = false;
        }
      } catch (error) {
        console.error('Errore durante il controllo fatturabile auto:', error);
        fatturabileCheck.value = { success: false, message: 'Errore durante il controllo' };
        showForm.value = false;
        responseMsg.value = 'Errore durante il controllo delle fatture automatiche';
        responseSuccess.value = false;
      } finally {
        checkLoader.value = false;
      }
    };

    onMounted(() => {
      loadPagamenti();
      loadPartitari();
      checkFatturabileAuto();
      document.addEventListener('click', onClickOutside);
    });

    watch(podInput, (newValue) => {
      if (newValue && newValue !== form.pod) {
        form.pod = newValue;
      }
    });

    watch(pdrInput, (newValue) => {
      if (newValue && newValue !== form.pdr) {
        form.pdr = newValue;
      }
    });
    const onClickOutside = (event) => {
      if (!event.target.closest('.autocomplete-wrapper') && !event.target.closest('.autocomplete-list')) {
        showClientDropdown.value = false;
        showContrattiDropdown.value = false;
        showPodDropdown.value = false;
        showPdrDropdown.value = false;

        for (let i = 0; i < showVociDropdown.value.length; i++) {
          showVociDropdown.value[i] = false;
        }
      }
    };
    const clearClient = () => {
      clientInput.value = '';
      form.cod_cliente = '';
      contratti.value = [];
      contrattoSelezionato.value = null;
      showClientDropdown.value = false;
      showContrattiDropdown.value = false;
    };

    const clearContratto = () => {
      contrattoSelezionato.value = null;
      showContrattiDropdown.value = false;
    };

    const clearPod = () => {
      podInput.value = '';
      form.pod = '';
      showPodDropdown.value = false;
    };

    const clearPdr = () => {
      pdrInput.value = '';
      form.pdr = '';
      showPdrDropdown.value = false;
    };

    const clearVoce = (idx) => {
      vociInput.value[idx] = '';
      form.voci[idx] = { cod_voce: '', qta: 1, aliquota: '', importo: '', descrizione: '' };
      showVociDropdown.value[idx] = false;
    };
    const clearPagamento = () => {
      form.cod_type_pay = '';
    };
    const onClearKey = (e, field, idx = null) => {
      const isClear =
        e.key === 'Escape' ||
        (e.ctrlKey && e.key === 'Backspace') ||
        (e.metaKey && e.key === 'Backspace');

      if (!isClear) return;

      if (field === 'client') return clearClient();
      if (field === 'contratto') return clearContratto();
      if (field === 'pod') return clearPod();
      if (field === 'pdr') return clearPdr();
      if (field === 'voce') return clearVoce(idx);
      if (field === 'pagamento') return clearPagamento();
    };

    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    self.$http = self.appContext.config.globalProperties.$http;
    const service = new Service(self.$http, self.$eventBus);

    const buildParams = (simula = false) => {
      if (podInput.value && !form.pod) {
        form.pod = podInput.value;
      }
      if (pdrInput.value && !form.pdr) {
        form.pdr = pdrInput.value;
      }

      const params = {
        partitario: form.partitario,
        type_doc: form.type_doc,
        data_emissione: form.data_emissione,
        data_scadenza: form.data_scadenza,
        cod_cliente: form.cod_cliente,
        pod: form.pod || podInput.value || '',
        pdr: form.pdr || pdrInput.value || '',
        cod_type_pay: form.cod_type_pay
      };

      if (contrattoSelezionato.value) {
        params.cod_contratto = contrattoSelezionato.value.id;
      }

      if (simula) params.simula = 1;
      form.voci.forEach((voce, i) => {
        const qty = Number(voce.qta) > 0 ? Number(voce.qta) : 1;
        const baseImporto = Number(voce.importo);

        if (voce.cod_voce && baseImporto) {
          params[`cod_voce_${i}`] = voce.cod_voce;
          params[`qta_${i}`] = qty;
          params[`importo_${i}`] = baseImporto; 
          params[`aliquota_${i}`] = voce.aliquota;
          params[`descrizione_${i}`] = voce.descrizione;
        }
      });

      return params;
    };

    const logFatturazioneExtra = async (tipoOperazione, numeroFattura, esito, pdfUrl = null) => {
      try {
       
        
        const user = store.state.auth.user ? JSON.parse(atob(store.state.auth.user)) : null;
        
        if (!user || !user.user || !user.user[0] || !user.user[0].id) {
          console.warn('Utente non trovato per il log');
          return;
        }
        
        const userId = user.user[0].id;

        const clienteSelezionato = clientiOptions.value.find(c => c.id === form.cod_cliente);
        const pagamentoSelezionato = pagamenti.value.find(p => p.id === form.cod_type_pay);
        
        for (let i = 0; i < form.voci.length; i++) {
          const voce = form.voci[i];
          
          if (voce.cod_voce && voce.importo) {
            const qty = Number(voce.qta) > 0 ? Number(voce.qta) : 1;
            const baseImporto = Number(voce.importo);
            const importoTotale = qty * baseImporto;

            const logData = {
              cod_user: userId,
              tipo_operazione: tipoOperazione,
              numero_fattura: numeroFattura || null,
              partitario: form.partitario,
              type_doc: form.type_doc,
              data_emissione: form.data_emissione,
              data_scadenza: form.data_scadenza,
              cod_cliente: form.cod_cliente,
              denominazione_cliente: clienteSelezionato ? clienteSelezionato.denominazione_completa : clientInput.value || null,
              cod_contratto: contrattoSelezionato.value ? contrattoSelezionato.value.id : null,
              pod: form.pod || podInput.value || null,
              pdr: form.pdr || pdrInput.value || null,
              cod_type_pay: form.cod_type_pay,
              nome_type_pay: pagamentoSelezionato ? pagamentoSelezionato.name : null,
              cod_voce: voce.cod_voce,
              descrizione_voce: vociInput.value[i] || null,
              qta: qty,
              aliquota: voce.aliquota || null,
              importo: baseImporto,
              importo_totale_voce: importoTotale,
              descrizione: voce.descrizione || null,
              esito: esito,
              pdf_url: pdfUrl || null
            };

            await service.insertLogFatturazioneExtra(logData);
          }
        }
      } catch (error) {
        console.error('Errore durante il log della fatturazione:', error);
        console.error('Stack trace:', error.stack);
      }
    };

    const onSubmit = async () => {
      loader.value = true;
      responseMsg.value = '';
      validationErrors.value = {};

      if (!form.partitario) {
        validationErrors.value.partitario = 'Il campo Partitario è obbligatorio.';
      }
      if (!form.type_doc) {
        validationErrors.value.type_doc = 'La tipologia è obbligatoria.';
      }
      if (!form.data_emissione) {
        validationErrors.value.data_emissione = 'La data di emissione è obbligatoria.';
      }
      if (!form.data_scadenza) {
        validationErrors.value.data_scadenza = 'La data di scadenza è obbligatoria.';
      }
      if (!form.cod_cliente) {
        validationErrors.value.cod_cliente = 'Il cliente è obbligatorio.';
      }
      if (!form.cod_type_pay) {
        validationErrors.value.cod_type_pay = 'Il pagamento è obbligatorio.';
      }

      if (Object.keys(validationErrors.value).length > 0) {
        responseMsg.value = 'Per favore, completa tutti i campi obbligatori.';
        responseSuccess.value = false;
        loader.value = false;
        return;
      }

      try {
        const params = buildParams(false);
        const res = await service.getFatturazioneExtra(params);
        responseSuccess.value = res.success;
        responseMsg.value = res.message || 'Fattura creata con successo';
        
        
        await logFatturazioneExtra(
          'fattura',
          res.numero || null,
          res.success ? 'successo' : 'errore',
          res.pdf_url || null
        );
        
        if (res.success && res.pdf_url) {
          window.open(res.pdf_url, '_blank');
        }
      } catch (e) {
        responseSuccess.value = false;
        responseMsg.value = e?.response?.data?.message || 'Errore nella creazione della fattura';
        
        await logFatturazioneExtra(
          'fattura',
          null,
          'errore',
          null
        );
      } finally {
        loader.value = false;
      }
    };

    const onSimula = async () => {
      loader.value = true;
      responseMsg.value = '';
      validationErrors.value = {};

      if (!form.partitario) {
        validationErrors.value.partitario = 'Il campo Partitario è obbligatorio.';
      }
      if (!form.type_doc) {
        validationErrors.value.type_doc = 'La tipologia è obbligatoria.';
      }
      if (!form.data_emissione) {
        validationErrors.value.data_emissione = 'La data di emissione è obbligatoria.';
      }
      if (!form.data_scadenza) {
        validationErrors.value.data_scadenza = 'La data di scadenza è obbligatoria.';
      }
      if (!form.cod_cliente) {
        validationErrors.value.cod_cliente = 'Il cliente è obbligatorio.';
      }
      if (!form.cod_type_pay) {
        validationErrors.value.cod_type_pay = 'Il pagamento è obbligatorio.';
      }

      if (Object.keys(validationErrors.value).length > 0) {
        responseMsg.value = 'Per favore, completa tutti i campi obbligatori.';
        responseSuccess.value = false;
        loader.value = false;
        return;
      }

      try {
        const params = buildParams(true);
        const res = await service.getFatturazioneExtra(params);
        responseSuccess.value = res.success;
        responseMsg.value = res.message || 'Simulazione completata';
                
        await logFatturazioneExtra(
          'simula',
          res.numero || null,
          res.success ? 'successo' : 'errore',
          res.pdf_url || null
        );
        
        if (res.success && res.pdf_url) {
          window.open(res.pdf_url, '_blank');
        }
      } catch (e) {
        responseSuccess.value = false;
        responseMsg.value = e?.response?.data?.message || 'Errore nella simulazione';
        
        await logFatturazioneExtra(
          'simula',
          null,
          'errore',
          null
        );
      } finally {
        loader.value = false;
      }
    };

    return {
      form,
      typeDocOptions,
      pagamenti,
      partitari,
      loader,
      responseMsg,
      responseSuccess,
      onSubmit,
      onSimula,
      clientInput,
      clientiOptions,
      showClientDropdown,
      onClientInput,
      selectClient,
      onClientFocus,
      onClientBlur,
      selectContratto,
      onContrattoFocus,
      onContrattoBlur,
      podInput,
      podOptions,
      showPodDropdown,
      onPodInput,
      selectPod,
      onPodFocus,
      onPodBlur,
      pdrInput,
      pdrOptions,
      showPdrDropdown,
      onPdrInput,
      selectPdr,
      onPdrFocus,
      onPdrBlur,
      vociInput,
      vociOptions,
      showVociDropdown,
      onVoceInput,
      selectVoce,
      onVoceFocus,
      onVoceBlur,
      checkLoader,
      fatturabileCheck,
      showForm,
      checkFatturabileAuto,
      contratti,
      showContrattiDropdown,
      contrattoSelezionato,
      contrattoValue,
      onClearKey,
      validationErrors,
      getDropdownZIndex,
      clearVoce,
      showVoceModal,
      modalSearchTerm,
      modalVociOptions,
      modalLoading,
      openVoceModal,
      closeVoceModal,
      onModalSearch,
      selectVoceFromModal,
      calculateVoceTotal
    };
  }
}
</script>


<style scoped>
/* Layout principale */
.fatturazione-extra-form {
  width: 100% !important;
  max-width: none !important;
  margin: 0 auto;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border-radius: 16px;
  padding: 2.5rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08), 0 2px 10px rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.8);
}

/* Sistema di griglia */
.fatturazione-extra-form .row {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 1.5rem !important;
  margin-bottom: 1.5rem !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.fatturazione-extra-form .row-wide {
  display: flex !important;
  flex-wrap: wrap !important;
  gap: 1.5rem !important;
  margin-bottom: 1.5rem !important;
  margin-left: 0 !important;
  margin-right: 0 !important;
}

.fatturazione-extra-form .col {
  flex: 1 1 300px !important;
  min-width: 250px !important;
  display: flex !important;
  flex-direction: column !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.fatturazione-extra-form .col-half {
  flex: 1 1 45% !important;
  min-width: 350px !important;
  display: flex !important;
  flex-direction: column !important;
}

.fatturazione-extra-form .col-6 {
  flex: 1 1 45% !important;
  min-width: 350px !important;
  display: flex !important;
  flex-direction: column !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.fatturazione-extra-form .col-12 {
  flex: 1 1 100% !important;
  min-width: 100% !important;
  display: flex !important;
  flex-direction: column !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
}

.fatturazione-extra-form .voce-col {
  flex: 2 1 400px !important;
  min-width: 350px !important;
}

.fatturazione-extra-form .aliquota-col {
  flex: 1 1 200px !important;
  min-width: 150px !important;
}

.fatturazione-extra-form .quantita-col {
  flex: 0 0 120px !important;
  min-width: 120px !important;
}

.fatturazione-extra-form .importo-col {
  flex: 1 1 200px !important;
  min-width: 150px !important;
}

.fatturazione-extra-form .descrizione-col {
  flex: 2 1 350px !important;
  min-width: 250px !important;
}

.fatturazione-extra-form .form-group {
  display: flex !important;
  flex-direction: column !important;
  width: 100% !important;
}

.fatturazione-extra-form .autocomplete-wrapper {
  position: relative;
  width: 100% !important;
  max-width: none !important;
  display: block !important;
}

.fatturazione-extra-form .autocomplete-wrapper .form-control {
  width: 100% !important;
  max-width: none !important;
}

.fatturazione-extra-form .mt-3 {
  margin-top: 2rem;
}

.fatturazione-extra-form .voci-section {
  margin-top: 2.5rem;
  margin-bottom: 2.5rem;
}

.fatturazione-extra-form .voce-row {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1rem;
  border: 1px solid rgba(226, 232, 240, 0.8);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03);
  transition: all 0.3s ease;
}

.fatturazione-extra-form .voce-row:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
}

.fatturazione-extra-form .btn {
  width: 100%;
  padding: 1rem 1.5rem;
  font-size: 1.1rem;
  font-weight: 600;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  position: relative;
  overflow: hidden;
}

.fatturazione-extra-form .btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
  transition: left 0.5s;
}

.fatturazione-extra-form .btn:hover::before {
  left: 100%;
}

.fatturazione-extra-form .btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: #fff;
}

.fatturazione-extra-form .btn-primary:hover {
  background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.3);
}

.fatturazione-extra-form .btn-danger {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  color: #fff;
}

.fatturazione-extra-form .btn-danger:hover {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(239, 68, 68, 0.3);
}

.fatturazione-extra-form .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

/* Loader e messaggi */
.fatturazione-extra-form .loader {
  margin-top: 1.5rem;
  color: #3b82f6;
  font-weight: 600;
  text-align: center;
  padding: 1rem;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-radius: 12px;
  border: 1px solid rgba(59, 130, 246, 0.2);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
}

.fatturazione-extra-form .response-msg {
  margin-top: 1.5rem;
  padding: 1.5rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%);
  color: #065f46;
  font-weight: 600;
  border: 1px solid rgba(16, 185, 129, 0.2);
  box-shadow: 0 4px 12px rgba(16, 185, 129, 0.1);
}

.fatturazione-extra-form .response-msg.error {
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  color: #991b1b;
  border: 1px solid rgba(239, 68, 68, 0.2);
  box-shadow: 0 4px 12px rgba(239, 68, 68, 0.1);
}

/* Autocomplete list */
.fatturazione-extra-form .autocomplete-list {
  position: absolute;
  z-index: 1000;
  top: 100%;
  left: 0;
  right: 0;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 0 0 12px 12px;
  max-height: 180px;
  overflow-y: auto;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  margin: 0;
  padding: 0;
  list-style: none;
  backdrop-filter: blur(10px);
  border-top: none;
  /* Assicura che il dropdown non si estenda fuori dallo schermo */
  max-width: 100%;
  word-wrap: break-word;
}

.fatturazione-extra-form .autocomplete-list li {
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
  border-bottom: 1px solid #f1f5f9;
}

.fatturazione-extra-form .autocomplete-list li:last-child {
  border-bottom: none;
}

.fatturazione-extra-form .autocomplete-list li:hover {
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  color: #1e293b;
  font-weight: 500;
}

/* Form controls */
.fatturazione-extra-form .form-control {
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.fatturazione-extra-form .form-control:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  transform: translateY(-1px);
}

.fatturazione-extra-form .form-control:hover {
  border-color: #cbd5e1;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04);
}

.fatturazione-extra-form .importo-totale-hint {
  margin-top: 0.35rem;
  font-size: 0.9rem;
  color: #334155;
  opacity: 0.85;
}

/* Labels */
.fatturazione-extra-form label {
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Scrollbar personalizzata */
.fatturazione-extra-form .autocomplete-list::-webkit-scrollbar {
  width: 6px;
}

.fatturazione-extra-form .autocomplete-list::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 3px;
}

.fatturazione-extra-form .autocomplete-list::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

.fatturazione-extra-form .autocomplete-list::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Voce Selector Styles */
.fatturazione-extra-form .voce-selector {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.fatturazione-extra-form .voce-display {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.875rem 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 10px;
  background: #fff;
  cursor: pointer;
  transition: all 0.3s ease;
  min-height: 48px;
}

.fatturazione-extra-form .voce-display:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.04);
}

.fatturazione-extra-form .voce-selected {
  color: #1e293b;
  font-weight: 500;
  flex: 1;
  text-align: left;
}

.fatturazione-extra-form .voce-placeholder {
  color: #94a3b8;
  font-style: italic;
  flex: 1;
  text-align: left;
}

.fatturazione-extra-form .voce-icon {
  font-size: 1.2rem;
  color: #64748b;
}

.fatturazione-extra-form .btn-clear-voce {
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.fatturazione-extra-form .btn-clear-voce:hover {
  background: #dc2626;
  transform: scale(1.1);
}

/* Modal Styles */
.fatturazione-extra-form .modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
}

.fatturazione-extra-form .modal-content {
  background: white;
  border-radius: 16px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.fatturazione-extra-form .modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid #e2e8f0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
}

.fatturazione-extra-form .modal-header h3 {
  margin: 0;
  color: #1e293b;
  font-size: 1.5rem;
  font-weight: 600;
}

.fatturazione-extra-form .modal-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #64748b;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 8px;
  transition: all 0.2s ease;
}

.fatturazione-extra-form .modal-close:hover {
  background: #f1f5f9;
  color: #1e293b;
}

.fatturazione-extra-form .modal-body {
  padding: 2rem;
  max-height: calc(80vh - 120px);
  overflow-y: auto;
}

.fatturazione-extra-form .search-section {
  margin-bottom: 2rem;
}

.fatturazione-extra-form .modal-search {
  width: 100%;
  padding: 1rem 1.5rem;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  font-size: 1.1rem;
  transition: all 0.3s ease;
  background: #fff;
}

.fatturazione-extra-form .modal-search:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.fatturazione-extra-form .voci-list {
  min-height: 300px;
}

.fatturazione-extra-form .loading-spinner {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem;
  color: #64748b;
}

.fatturazione-extra-form .spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.fatturazione-extra-form .no-results {
  text-align: center;
  padding: 3rem;
  color: #64748b;
  font-style: italic;
}

.fatturazione-extra-form .voci-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.fatturazione-extra-form .voce-item {
  background: #fff;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.02);
}

.fatturazione-extra-form .voce-item:hover {
  border-color: #3b82f6;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.15);
  transform: translateY(-2px);
}

.fatturazione-extra-form .voce-code {
  font-weight: 600;
  color: #3b82f6;
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
}

.fatturazione-extra-form .voce-desc {
  font-weight: 500;
  color: #1e293b;
  margin-bottom: 0.5rem;
  line-height: 1.4;
}

.fatturazione-extra-form .voce-category {
  font-size: 0.85rem;
  color: #64748b;
  background: #f1f5f9;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  display: inline-block;
}

/* Media queries per responsive */
@media (max-width: 1200px) {
  .fatturazione-extra-form .col-6 {
    flex: 1 1 100% !important;
    min-width: 100% !important;
  }
  
  .fatturazione-extra-form .col-half {
    flex: 1 1 100% !important;
    min-width: 100% !important;
  }
  
  .fatturazione-extra-form .voci-grid {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }
}

@media (max-width: 768px) {
  .fatturazione-extra-form {
    padding: 1.5rem !important;
  }

  .fatturazione-extra-form .row,
  .fatturazione-extra-form .row-wide {
    gap: 1rem !important;
  }
  
  .fatturazione-extra-form .col {
    flex: 1 1 100% !important;
    min-width: 100% !important;
  }
  
  .fatturazione-extra-form .modal-content {
    width: 95%;
    margin: 1rem;
  }
  
  .fatturazione-extra-form .modal-body {
    padding: 1.5rem;
  }
  
  .fatturazione-extra-form .voci-grid {
    grid-template-columns: 1fr;
  }
}
</style>